import { useEffect, useReducer, useState } from 'react';
import { callDeepLink } from '@croquiscom/web2app';
import { useBottomSheet } from '@croquiscom-pvt/zds';
import { useAtomValue, useUpdateAtom } from 'jotai/utils';

import { ZigzagEvent } from '@common/app-manager';
import { Toast } from '@common/overlay-manager';
import { gotoLogin, numberWithCommas } from '@common/utils';
import {
  applyDiscountPolicy,
  bpToDecimal,
  change_shop_bookmark_atom,
  FinancialCompanyCode,
  formatKoreanNumber,
  GetPaymentNotificationList_payment_notification_list_item_list_payment_notification_content_list as payment_notification_content_list,
  PaymentMethod,
  PdpCouponAvailableStatus as CouponStatus,
  PdpCouponRegistrationType,
  PopupViewType,
  shop_bookmark_atom,
  useCatalogProductIdParams,
  useIsAuthenticated,
  useMaximumBenefit,
  usePdpBaseInfo,
  usePdpConfig,
  usePdpUserAccountBenefitInfo,
  usePopupView,
  VendorEventType,
} from '@domains/pdp-common';

import { BottomInfoView } from '../components/BottomInfoView';
import { minimumDiscountedPrice, minRetentionMileage, minUseMileage, minUsePoint } from '../constant/benefit';
import { is_pdp_benefit_coupon_download, pdp_benefit_coupon_policy_id } from '../constant/stoargeKey';
import { useMaximumBenefitLog } from '../log/useMaximumBenefitLog';
import { useSendCouponDownload } from '../log/useSendCouponDownload';
import { useSendPageviewMaximumBenefit } from '../log/useSendPageviewMaximumBenefit';
import { benefitReducer, initialBenefitState } from '../reducer/benefitReducer';
import {
  applyPaymentEventDiscount,
  applyRewardDiscount,
  calculateMaxMileageUsage,
  calculateMaxRewardUsage,
} from '../utils/benefitCalculatorUtils';
import { downloadGeneralCoupon, downloadMembershipCoupon } from '../utils/downloadCouponUtils';
import { generateBenefitInfoUtils } from '../utils/generateBenefitInfoUtils';
import {
  calculatePaymentBenefitInfo,
  filterPaymentListByMinAmount,
  normalizePaymentList,
} from '../utils/paymentBenefitUtils';

export interface PaymentEventItem {
  id: number;
  financial_company_code: FinancialCompanyCode | null;
  min_amount: number;
  discount_amount: number;
  payment_method: PaymentMethod;
  payment_method_type: string | null;
  payment_notification_content_list?: payment_notification_content_list[];
  event_type?: VendorEventType;
}

export const useBenefitService = () => {
  const { open: openBottomInfo } = useBottomSheet('custom');

  const language = 'ko';
  const { isZigzagApp } = usePdpConfig();
  const { showPopup } = usePopupView();
  const { isLoggedIn } = useIsAuthenticated();
  const { clickDropDownUBL, clickBenefitItemToggleUBL } = useMaximumBenefitLog();
  const generateBenefitInfo = generateBenefitInfoUtils();
  const param = useCatalogProductIdParams();
  const {
    payment_event_list_for_display,
    payment_notification_list,
    pdp_max_coupon_discount,
    pdp_max_coupon_discount: { discount_amount: maxCouponDiscountAmount },
    pdp_duplication_coupon_discount,
    pdp_duplication_coupon_discount: { discount_amount: duplicationCouponDiscountAmount },
    refetch: refetchPdpCouponBenefit,
    isFetched: isFetchedMaximumBenefit,
  } = useMaximumBenefit();

  const {
    product,
    product: {
      id: product_id,
      coupon_available_status,
      product_price: {
        coupon_discount_info_list,
        bogo_promotion_discount_info,
        max_price_info: { price: max_price },
        final_discount_info: { discount_price: finalDiscountPrice },
        store_discount_info: { discount_amount: storeDiscountAmount },
        first_order_discount,
        first_order_discount: { discount_amount: firstOrderDiscountAmount },
        product_promotion_discount_info: { discount_amount: promotionDiscountAmount },
        coupon_button_color: { normal: coupon_button_of_atf },
      },
    },
    shop,
    key_color: { discount_info_of_atf },
    isFetched: isFetchedPdpBaseInfo,
  } = usePdpBaseInfo(param);

  const {
    pdp_user_base_info: { pdp_user_account_point, pdp_user_account_mileage },
    isFetched: isFetchedPointAndMileage,
  } = usePdpUserAccountBenefitInfo();

  const { targetRef: coupon_target_ref, onClickCouponDownloadUBL } = useSendCouponDownload({
    product_id: product_id,
    policy_id: pdp_max_coupon_discount?.user_account_coupon_policy_id ?? '',
    issuable: pdp_max_coupon_discount?.issue_status === 'ISSUABLE',
  });

  const { targetRef: duplication_coupon_target_ref, onClickCouponDownloadUBL: onClickDuplicationCouponDownloadUBL } =
    useSendCouponDownload({
      product_id: product_id,
      policy_id: pdp_duplication_coupon_discount?.user_account_coupon_policy_id ?? '',
      issuable: pdp_duplication_coupon_discount?.issue_status === 'ISSUABLE',
    });

  const shopBookmark = useAtomValue(shop_bookmark_atom);
  const setShopBookmark = useUpdateAtom(change_shop_bookmark_atom);

  const [benefitBoxOpen, setBenefitBoxOpen] = useState(false);
  const [bookmarkDrawerOpen, setBookmarkDrawerOpen] = useState(false);

  const [benefitState, dispatchBenefit] = useReducer(benefitReducer, initialBenefitState);
  const {
    userClick,
    checkedMaxCoupon,
    checkedDuplicationCoupon,
    checkedPoint,
    checkedMileage,
    checkedListPaymentEvent,
  } = benefitState;

  const paymentList = normalizePaymentList(
    { paymentEventList: payment_event_list_for_display, finalDiscountPrice },
    payment_notification_list,
  );

  const filterPaymentEventList = filterPaymentListByMinAmount(
    paymentList,
    benefitState.priceBenefits.exceptionPaymentEventBenefitPrice,
  );

  const paymentBenefitInfo = calculatePaymentBenefitInfo(
    language,
    filterPaymentEventList,
    benefitState.priceBenefits.exceptionPaymentEventBenefitPrice,
  );

  const currentPoint = pdp_user_account_point?.available_point ?? 0;
  const currentMileage = pdp_user_account_mileage?.available_mileage ?? 0;

  const moreThanMinPoint = currentPoint >= minUsePoint;
  const moreThanMinMileage = currentMileage >= minRetentionMileage;

  const isMileageUsable = benefitState.credits.mileage >= minUseMileage && moreThanMinMileage;
  const isPointUsable = benefitState.credits.point >= minUsePoint;

  const isDisplayPaymentEvent = !!filterPaymentEventList.length;
  const isFirstOrderDiscount = !!first_order_discount.promotion_id;
  const displayMaxCoupon =
    Boolean(maxCouponDiscountAmount || duplicationCouponDiscountAmount) &&
    coupon_available_status !== CouponStatus.COUPON_NOT_APPLICABLE;

  const availableBenefit = {
    checkout: isDisplayPaymentEvent,
    coupon: displayMaxCoupon,
    point: isPointUsable,
    mileage: isMileageUsable,
    discount: storeDiscountAmount > 0,
    zpay_discount: promotionDiscountAmount > 0,
    first_purchase: isFirstOrderDiscount,
  };

  const hasAvailableBenefit = Object.values(availableBenefit).some(Boolean);

  const checkOverFinalPrice = benefitState.priceBenefits.maximumBenefitPrice > finalDiscountPrice;
  const checkSameFinalPrice =
    !paymentBenefitInfo && benefitState.priceBenefits.maximumBenefitPrice === finalDiscountPrice;

  const hideMaximumBenefit =
    !hasAvailableBenefit || !shop.is_mileage_shop || (!userClick && (checkOverFinalPrice || checkSameFinalPrice));

  const { targetRef: pageviewTargetRef } = useSendPageviewMaximumBenefit({
    product_id: product.id,
    maximumBenefitPrice: benefitState.priceBenefits.maximumBenefitPrice,
    benefitType: availableBenefit,
    has_data: hasAvailableBenefit,
  });

  const onClickShowBottomSheet = () => {
    showPopup(PopupViewType.MAXIMUM_BENEFIT, () => {
      openBottomInfo({
        children: (
          <div style={{ padding: '16px' }}>
            <BottomInfoView />
          </div>
        ),
      });
    });
  };

  const closeBookmarkDrawer = () => {
    setBookmarkDrawerOpen(false);
  };

  const onClickCheckedPaymentList = (index: number) => {
    const checkedList = [...checkedListPaymentEvent];
    const toggleCheckedItem = !checkedList[index];

    if (toggleCheckedItem) {
      const minAmount = paymentList.find((item) => item.id === index)?.min_amount ?? 0;

      const checkMinAmount = benefitState.priceBenefits.exceptionPaymentEventBenefitPrice >= minAmount;

      if (!checkMinAmount) {
        Toast.show(`결제 금액이 ${formatKoreanNumber(minAmount)} 이상인 경우에 사용 가능합니다.`);
        return;
      }
    }

    checkedList.fill(false);
    checkedList[index] = toggleCheckedItem;

    dispatchBenefit({
      type: 'SET_CHECKED_PAYMENT_EVENT',
      payload: checkedList,
    });
  };

  const couponDownload = async (id?: string) => {
    const policy_id = id ?? pdp_max_coupon_discount?.user_account_coupon_policy_id ?? '';

    if (pdp_max_coupon_discount?.registration_type === PdpCouponRegistrationType.BOOKMARK) {
      await downloadGeneralCoupon({ shop_id: shop.id, product_id: product_id, policy_id });
    } else if (pdp_max_coupon_discount?.registration_type === PdpCouponRegistrationType.MEMBERSHIP) {
      await downloadMembershipCoupon();
    } else {
      await downloadGeneralCoupon({ shop_id: shop.id, product_id: product_id, policy_id });
    }
    refetchPdpCouponBenefit();
  };

  const addBookmarkAndDownloadCoupon = async () => {
    if (!checkLoginBeforeCouponDownload()) {
      return;
    }

    ZigzagEvent.abk(shop.main_domain);
    setShopBookmark(true);
    await couponDownload();
    closeBookmarkDrawer();
  };

  const checkLoginBeforeCouponDownload = () => {
    if (!isLoggedIn) {
      const policy_id = pdp_max_coupon_discount?.user_account_coupon_policy_id ?? '';

      sessionStorage.setItem(is_pdp_benefit_coupon_download, 'true');

      if (pdp_max_coupon_discount?.registration_type !== PdpCouponRegistrationType.MEMBERSHIP) {
        sessionStorage.setItem(pdp_benefit_coupon_policy_id, policy_id);
      }
      redirectToLogin();
      return false;
    }
    return true;
  };

  const redirectToLogin = () => {
    if (isZigzagApp) {
      callDeepLink('/login')();
      return;
    }
    gotoLogin({ redirect_url: window.location.href });
  };

  const onClickCouponIcon = (
    user_account_coupon_policy_id: string,
    registration_type?: PdpCouponRegistrationType | null,
  ) => {
    if (!checkLoginBeforeCouponDownload()) {
      return;
    }

    if (!shopBookmark && registration_type === PdpCouponRegistrationType.BOOKMARK) {
      setBookmarkDrawerOpen(true);
      return;
    }

    couponDownload(user_account_coupon_policy_id ?? '');
  };

  const getBenefitInfo = () => {
    const couponInfo = {
      displayMaxCoupon,
      buttonColor: coupon_button_of_atf,
      coupon: {
        targetRef: coupon_target_ref,
        discountAmount: maxCouponDiscountAmount,
        title: pdp_max_coupon_discount?.title ?? '',
        issue_status: pdp_max_coupon_discount?.issue_status,
        checked: checkedMaxCoupon,
        onChecked: (checked: boolean) => {
          dispatchBenefit({
            type: 'CLICK_MAX_COUPON',
            payload: !checked,
          });
          clickBenefitItemToggleUBL(product_id, checked ? 'off' : 'on', 'coupon');
        },
        buttonOnClick: () => {
          onClickCouponDownloadUBL();
          onClickCouponIcon(
            pdp_max_coupon_discount?.user_account_coupon_policy_id ?? '',
            pdp_max_coupon_discount?.registration_type,
          );
        },
      },
      duplication_coupon: {
        targetRef: duplication_coupon_target_ref,
        discountAmount: duplicationCouponDiscountAmount,
        title: pdp_duplication_coupon_discount?.title ?? '',
        issue_status: pdp_duplication_coupon_discount?.issue_status,
        checked: checkedDuplicationCoupon,
        onChecked: (checked: boolean) => {
          dispatchBenefit({
            type: 'CLICK_DUPLICATION_COUPON',
            payload: !checked,
          });
          clickBenefitItemToggleUBL(product_id, checked ? 'off' : 'on', 'coupon');
        },
        buttonOnClick: () => {
          onClickDuplicationCouponDownloadUBL();
          onClickCouponIcon(
            pdp_duplication_coupon_discount?.user_account_coupon_policy_id ?? '',
            pdp_duplication_coupon_discount?.registration_type,
          );
        },
      },
    };
    const pointInfo = {
      point: {
        currentPoint,
        usePoint: benefitState.credits.point ?? 0,
        shouldNotifyMinPoint: !moreThanMinPoint,
        shouldNotifyPointUsable: !isPointUsable,
      },
      disabled: !moreThanMinPoint || !isPointUsable,
      checked: checkedPoint,
      onChecked: (checked: boolean) => {
        dispatchBenefit({
          type: 'CLICK_POINT',
          payload: !checked,
        });

        clickBenefitItemToggleUBL(product_id, checked ? 'off' : 'on', 'point');
      },
    };
    const mileageInfo = {
      mileage: {
        current: currentMileage,
        use: benefitState.credits.mileage ?? 0,
        isUsable: isMileageUsable,
        moreThanMin: moreThanMinMileage,
      },
      shop: {
        name: shop.name,
        isMileageUsable: Boolean(shop.usable_mileage?.use_mileage),
        addRate: (shop.usable_mileage?.seller_specific_use_mileage_rate_bp ?? 0) / 100,
        defaultRate: (shop.usable_mileage?.default_use_mileage_rate_bp ?? 0) / 100,
      },
      checked: checkedMileage,
      onChecked: (checked: boolean) => {
        dispatchBenefit({
          type: 'CLICK_MILEAGE',
          payload: !checked,
        });

        clickBenefitItemToggleUBL(product_id, checked ? 'off' : 'on', 'mileage');
      },
    };

    return [
      ...generateBenefitInfo.getDefaultBenefitInfo(
        storeDiscountAmount,
        promotionDiscountAmount,
        bogo_promotion_discount_info.discount_amount,
      ),
      ...generateBenefitInfo.getFirstOrderBenefitInfo(firstOrderDiscountAmount, redirectToLogin),
      ...generateBenefitInfo.getCouponBenefitInfo(couponInfo),
      ...generateBenefitInfo.getPointBenefitInfo(pointInfo),
      ...generateBenefitInfo.getMileageBenefitInfo(mileageInfo),
      ...generateBenefitInfo.getPaymentEventBenefitInfo({
        filterPaymentEventList,
        checkedList: checkedListPaymentEvent,
        onChecked: (checked: boolean, id: number) => {
          onClickCheckedPaymentList(id);
          clickBenefitItemToggleUBL(product_id, checked ? 'off' : 'on', 'checkout');
        },
      }),
    ];
  };

  useEffect(() => {
    // MEMO: 할인 금액이 변동되어 filterPaymentEventList 갯수가 바뀌었을때 결제 혜택 체크 초기화
    dispatchBenefit({
      type: 'SET_CHECKED_PAYMENT_EVENT',
      payload: [],
    });
  }, [filterPaymentEventList.length]);

  useEffect(() => {
    if (isLoggedIn && sessionStorage.getItem(is_pdp_benefit_coupon_download)) {
      const coupon_download_policy_id = sessionStorage.getItem(pdp_benefit_coupon_policy_id);
      coupon_download_policy_id ? couponDownload(coupon_download_policy_id as string) : addBookmarkAndDownloadCoupon();

      setBenefitBoxOpen(true);

      sessionStorage.removeItem(is_pdp_benefit_coupon_download);
      sessionStorage.removeItem(pdp_benefit_coupon_policy_id);
    }
  }, [isLoggedIn]);

  // 예상 최저가 계산
  useEffect(() => {
    // 초기 가격 설정
    const defaultDiscountPrice = max_price - storeDiscountAmount - promotionDiscountAmount - firstOrderDiscountAmount;
    let currentPrice = {
      base: defaultDiscountPrice,
      forPaymentEvent: defaultDiscountPrice,
    };

    // 1. 쿠폰 할인 적용
    if (displayMaxCoupon) {
      if (checkedMaxCoupon) {
        const discountedPrice = currentPrice.base - maxCouponDiscountAmount;

        if (discountedPrice >= minimumDiscountedPrice) {
          currentPrice = {
            base: discountedPrice,
            forPaymentEvent: discountedPrice,
          };
        } else {
          dispatchBenefit({
            type: 'SET_CHECKED_MAX_COUPON',
            payload: false,
          });
        }
      }

      if (checkedDuplicationCoupon) {
        const discountedPrice = currentPrice.base - duplicationCouponDiscountAmount;

        if (discountedPrice >= minimumDiscountedPrice) {
          currentPrice = {
            base: discountedPrice,
            forPaymentEvent: discountedPrice,
          };
        } else {
          dispatchBenefit({
            type: 'SET_CHECKED_DUPLICATION_COUPON',
            payload: false,
          });
        }
      }
    }

    // 2. 결제 이벤트 할인 적용 (결제 이벤트는 포인트/마일리지와 별도 계산)
    if (checkedListPaymentEvent.length) {
      const checkedId = checkedListPaymentEvent.findIndex(Boolean);
      const discountedPrice = applyPaymentEventDiscount(paymentList, currentPrice.base, checkedId);

      if (discountedPrice >= minimumDiscountedPrice) {
        currentPrice.base = discountedPrice;
      }
    }

    // 3. 포인트/마일리지 계산
    if (isFetchedPointAndMileage) {
      let maxUsePoint, maxUseMileage;
      const couponDiscountAmount = coupon_discount_info_list.reduce((acc, cur) => acc + cur.discount_amount, 0);
      const maxAvailableMileage =
        (finalDiscountPrice + couponDiscountAmount) * bpToDecimal(shop.usable_mileage?.total_use_mileage_rate_bp ?? 0);
      // 두 할인 중 어떤 것을 먼저 적용할지 결정
      const applyPointFirst = currentPoint > maxAvailableMileage;

      if (applyPointFirst) {
        maxUsePoint = calculateMaxRewardUsage(currentPoint, currentPrice.base);

        // 포인트 먼저 적용
        if (checkedPoint) {
          currentPrice.base = applyRewardDiscount(maxUsePoint, currentPrice.base);
          currentPrice.forPaymentEvent = applyRewardDiscount(maxUsePoint, currentPrice.forPaymentEvent);
        }

        // 남은 금액에 대해 마일리지 다시 계산
        maxUseMileage = calculateMaxMileageUsage(maxAvailableMileage, currentMileage, currentPrice.base);

        // 포인트 적용 후 마일리지 다시 계산
        if (checkedMileage) {
          currentPrice.base = applyRewardDiscount(maxUseMileage, currentPrice.base);
          currentPrice.forPaymentEvent = applyRewardDiscount(maxUseMileage, currentPrice.forPaymentEvent);
        }
      } else {
        maxUseMileage = calculateMaxMileageUsage(maxAvailableMileage, currentMileage, currentPrice.base);

        // 마일리지 먼저 적용
        if (checkedMileage) {
          currentPrice.base = applyRewardDiscount(maxUseMileage, currentPrice.base);
          currentPrice.forPaymentEvent = applyRewardDiscount(maxUseMileage, currentPrice.forPaymentEvent);
        }

        // 남은 금액에 대해 포인트 다시 계산
        maxUsePoint = calculateMaxRewardUsage(currentPoint, currentPrice.base);

        // 마일리지 적용 후 포인트 다시 계산
        if (checkedPoint) {
          currentPrice.base = applyRewardDiscount(maxUsePoint, currentPrice.base);
          currentPrice.forPaymentEvent = applyRewardDiscount(maxUsePoint, currentPrice.forPaymentEvent);
        }
      }

      const isPointApplicable = maxUsePoint >= minUsePoint && maxUsePoint > minimumDiscountedPrice;
      const isMileageApplicable = maxUseMileage >= minUseMileage && maxUseMileage > minimumDiscountedPrice;

      // 사용 불가능한 경우 체크 해제
      if (!isPointApplicable) {
        dispatchBenefit({
          type: 'SET_CHECKED_POINT',
          payload: false,
        });
      }

      if (!isMileageApplicable) {
        dispatchBenefit({
          type: 'SET_CHECKED_MILEAGE',
          payload: false,
        });
      }

      dispatchBenefit({
        type: 'SET_CREDITS',
        payload: {
          point: maxUsePoint,
          mileage: maxUseMileage,
        },
      });
    }

    // 할인율 계산
    const discountRate = ((max_price - currentPrice.base) / max_price) * 100;

    dispatchBenefit({
      type: 'SET_PRICE_BENEFITS',
      payload: {
        maximumBenefitPrice: currentPrice.base,
        exceptionPaymentEventBenefitPrice: currentPrice.forPaymentEvent,
        maximumBenefitDiscountRate: applyDiscountPolicy(discountRate),
      },
    });
  }, [
    isFetchedPdpBaseInfo,
    isFetchedMaximumBenefit,
    isFetchedPointAndMileage,
    checkedMaxCoupon,
    checkedDuplicationCoupon,
    checkedPoint,
    checkedMileage,
    checkedListPaymentEvent,
  ]);

  return {
    pageviewTargetRef,

    isHide: hideMaximumBenefit,
    benefits: getBenefitInfo(),
    benefitBoxOpen,
    bookmarkDrawerOpen,

    closeBookmarkDrawer,
    addBookmarkAndDownloadCoupon,

    summary: {
      isFetched: isFetchedMaximumBenefit && isFetchedPdpBaseInfo && isFetchedPointAndMileage,
      sales: benefitState.priceBenefits.maximumBenefitDiscountRate,
      salesColor: discount_info_of_atf,
      content: `${numberWithCommas(benefitState.priceBenefits.maximumBenefitPrice)}원`,
      paymentBenefitInfo: benefitBoxOpen ? null : paymentBenefitInfo,
      onClick: () => {
        setBenefitBoxOpen(!benefitBoxOpen);
        clickDropDownUBL(product_id, benefitBoxOpen ? 'up' : 'down');
      },
      onClickInfo: onClickShowBottomSheet,
    },
  };
};
